import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react'

export enum DesignSystemTheme {
  Light = 'light',
  Dark = 'dark',
  Zinc = 'zinc',
  System = 'system',
}

export enum ResolvedDesignSystemTheme {
  Light = 'light',
  Dark = 'dark',
}

interface DesignSystemThemeSnapshot {
  theme: DesignSystemTheme
  resolvedTheme: ResolvedDesignSystemTheme
}

type LegacyMediaQueryList = MediaQueryList & {
  addListener?: (listener: (event: MediaQueryListEvent) => void) => void
}

const DESIGN_SYSTEM_THEME_STORAGE_KEY = 'design-system-theme'
const DEFAULT_DESIGN_SYSTEM_THEME = DesignSystemTheme.Dark
const subscribers = new Set<() => void>()

let hasInstalledListeners = false
let cachedSnapshot: DesignSystemThemeSnapshot | undefined

function isDesignSystemTheme(value: string | null): value is DesignSystemTheme {
  return (
    value === DesignSystemTheme.Light ||
    value === DesignSystemTheme.Dark ||
    value === DesignSystemTheme.Zinc ||
    value === DesignSystemTheme.System
  )
}

function getStoredTheme(): DesignSystemTheme {
  if (typeof window === 'undefined') {
    return DEFAULT_DESIGN_SYSTEM_THEME
  }

  const storedTheme = window.localStorage.getItem(
    DESIGN_SYSTEM_THEME_STORAGE_KEY
  )

  return isDesignSystemTheme(storedTheme)
    ? storedTheme
    : DEFAULT_DESIGN_SYSTEM_THEME
}

function getSystemTheme(): ResolvedDesignSystemTheme {
  if (typeof window === 'undefined') {
    return ResolvedDesignSystemTheme.Dark
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? ResolvedDesignSystemTheme.Dark
    : ResolvedDesignSystemTheme.Light
}

function getResolvedTheme(theme: DesignSystemTheme): ResolvedDesignSystemTheme {
  return theme === DesignSystemTheme.System
    ? getSystemTheme()
    : theme === DesignSystemTheme.Dark || theme === DesignSystemTheme.Zinc
      ? ResolvedDesignSystemTheme.Dark
      : ResolvedDesignSystemTheme.Light
}

function getSnapshot(): DesignSystemThemeSnapshot {
  const theme = getStoredTheme()
  const resolvedTheme = getResolvedTheme(theme)

  if (
    cachedSnapshot?.theme === theme &&
    cachedSnapshot.resolvedTheme === resolvedTheme
  ) {
    return cachedSnapshot
  }

  cachedSnapshot = {
    theme,
    resolvedTheme,
  }

  return cachedSnapshot
}

function applyDesignSystemThemeToDocument(
  theme: DesignSystemTheme,
  resolvedTheme: ResolvedDesignSystemTheme
): void {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  root.classList.toggle(
    'dark',
    resolvedTheme === ResolvedDesignSystemTheme.Dark
  )
  root.classList.toggle('zinc', theme === DesignSystemTheme.Zinc)
  root.style.colorScheme = resolvedTheme
}

function emitChange() {
  for (const subscriber of subscribers) {
    subscriber()
  }
}

function installGlobalListeners() {
  if (hasInstalledListeners || typeof window === 'undefined') {
    return
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  const handleMediaChange = () => {
    const snapshot = getSnapshot()
    applyDesignSystemThemeToDocument(snapshot.theme, snapshot.resolvedTheme)
    emitChange()
  }
  const handleStorage = (event: StorageEvent) => {
    if (event.key === DESIGN_SYSTEM_THEME_STORAGE_KEY) {
      const snapshot = getSnapshot()
      applyDesignSystemThemeToDocument(snapshot.theme, snapshot.resolvedTheme)
      emitChange()
    }
  }

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleMediaChange)
  } else {
    ;(mediaQuery as LegacyMediaQueryList).addListener?.(handleMediaChange)
  }

  window.addEventListener('storage', handleStorage)
  hasInstalledListeners = true
}

function subscribe(callback: () => void) {
  installGlobalListeners()
  subscribers.add(callback)

  return () => {
    subscribers.delete(callback)
  }
}

function persistTheme(theme: DesignSystemTheme) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(DESIGN_SYSTEM_THEME_STORAGE_KEY, theme)
  applyDesignSystemThemeToDocument(theme, getResolvedTheme(theme))
  emitChange()
}

export function DesignSystemThemeRoot() {
  const { theme, resolvedTheme } = useDesignSystemTheme()

  useLayoutEffect(() => {
    applyDesignSystemThemeToDocument(theme, resolvedTheme)
  }, [theme, resolvedTheme])

  return null
}

export function useDesignSystemThemeDocumentOverride(
  themeOverride: DesignSystemTheme | undefined
) {
  const { theme, resolvedTheme } = useDesignSystemTheme()

  useLayoutEffect(() => {
    if (!themeOverride) {
      return
    }

    applyDesignSystemThemeToDocument(
      themeOverride,
      getResolvedTheme(themeOverride)
    )

    return () => applyDesignSystemThemeToDocument(theme, resolvedTheme)
  }, [resolvedTheme, theme, themeOverride])
}

export function useDesignSystemTheme() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const setTheme = useCallback((theme: DesignSystemTheme) => {
    persistTheme(theme)
  }, [])

  const toggleTheme = useCallback(() => {
    persistTheme(
      snapshot.resolvedTheme === ResolvedDesignSystemTheme.Dark
        ? DesignSystemTheme.Light
        : DesignSystemTheme.Dark
    )
  }, [snapshot.resolvedTheme])

  return {
    theme: snapshot.theme,
    resolvedTheme: snapshot.resolvedTheme,
    setTheme,
    toggleTheme,
  }
}
